import React, {useState, useEffect} from 'react';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import client, { url } from '../../lib/api/client';
import RunningTrackRealtimeComponent from './RunningTrackRealtimeComponent';

const Container = styled.div`
  padding: 20px;
  border: 2px solid #e0e0e0;
  border-radius: 12px;
  background-color: #fff;
  margin-bottom: 20px;
`;

const Title = styled.h2`
  margin: 0 0 20px 0;
  color: #333;
  font-size: 22px;
  font-weight: 700;
  padding-bottom: 15px;
  border-bottom: 2px solid #e0e0e0;
`;

const SettingSection = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  margin-bottom: 20px;
`;

const TimeInputContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-weight: 600;
  color: #555;
  font-size: 14px;
`;

const TimeInput = styled.input`
  padding: 10px 12px;
  border: 2px solid #e0e0e0;
  border-radius: 6px;
  font-size: 14px;
  width: 180px;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: #1976d2;
  }
`;

const InfoBox = styled.div`
  background-color: #f5f5f5;
  border-left: 4px solid #1976d2;
  padding: 16px 20px;
  margin-bottom: 24px;
  border-radius: 4px;
`;

const InfoTitle = styled.div`
  font-size: 15px;
  font-weight: 700;
  color: #333;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const InfoList = styled.ul`
  margin: 0;
  padding-left: 20px;
  list-style: none;
`;

const InfoItem = styled.li`
  font-size: 14px;
  color: #555;
  line-height: 1.8;
  position: relative;
  padding-left: 16px;

  &:before {
    content: '•';
    position: absolute;
    left: 0;
    color: #1976d2;
    font-weight: bold;
  }
`;

const ApplyButton = styled.button`
  padding: 12px 24px;
  background-color: ${props => props.disabled ? '#ccc' : '#1976d2'};
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: background-color 0.2s ease;
  margin-top: 20px;

  &:hover {
    background-color: ${props => props.disabled ? '#ccc' : '#1565c0'};
  }
`;

const PointInput = styled(TimeInput)`
  text-align: right;
`;

const InputRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #555;
`;

const SwitchRow = styled.div`
  display: inline-flex;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  overflow: hidden;
`;

const SwitchButton = styled.button`
  padding: 10px 22px;
  border: none;
  font-size: 14px;
  font-weight: 600;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  background-color: #fff;
  color: #555;
`;

// 선택된 쪽 색 — DOM 에 넘어가는 임의 prop(active 등) 경고를 피하려고 style 로 준다.
const switchStyle = (selected, onSide) => selected ? { backgroundColor: onSide ? '#1976d2' : '#757575', color: '#fff' } : undefined;

const FieldError = styled.div`
  font-size: 13px;
  color: #d32f2f;
`;

// ── 체험 보상 포인트(클럽별) ──────────────────────────────────────────────
//   러닝머신 앱 체험 소개 화면의 「첫 운동 15분 완료하면 N 포인트」 금액이자 실제 지급액.
//   켜기/끄기 + 금액(2026-10-08). ★켜면 0P 로 저장할 수 없다(1 이상). 끄면 지급 안 함 + 앱의 포인트 안내 숨김.
//   한 번도 설정 안 한 클럽은 서버 기본값 = 켜짐 + 5,000P(trialReward.ctrl.js loadSetting).
//   지급 기준(15분)은 고정이라 여기서 정하지 않는다.
const TRIAL_REWARD_MAX_POINT = 1000000;
const TRIAL_REWARD_DEFAULT_POINT = 5000;

const TrialRewardSetting = ({ club }) => {
  const [on, setOn] = useState(true);
  const [point, setPoint] = useState('');
  const [original, setOriginal] = useState({ on: true, point: '' });
  const [isLoading, setIsLoading] = useState(false);

  const load = async () => {
    if (!club) return;
    try {
      setIsLoading(true);
      const res = await client.get(`${url}/api/runningtrack/setting/trialreward/${club.clubID}`);
      const d = res.data || {};
      // on·amount 가 없으면(구 서버) point 로 대신한다 — 0 이면 꺼짐.
      const nextOn = d.on != null ? !!d.on : Number(d.point) > 0;
      const amount = d.amount != null ? d.amount : (Number(d.point) > 0 ? d.point : TRIAL_REWARD_DEFAULT_POINT);
      setOn(nextOn);
      setPoint(String(amount));
      setOriginal({ on: nextOn, point: String(amount) });
    } catch (error) {
      console.error('Failed to load trial reward:', error);
      alert('체험 보상 설정을 불러오는데 실패했습니다.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [club]);

  const parsed = Number(point);
  const amountOk = point !== '' && Number.isInteger(parsed) && parsed >= 1 && parsed <= TRIAL_REWARD_MAX_POINT;
  // ★켜짐이면 금액이 1 이상이어야 저장된다. 꺼짐이면 금액 칸은 보관용이라 비었거나 틀려도 저장을 막지 않는다(그땐 금액을 안 보낸다).
  const isValid = on ? amountOk : true;
  const isModified = on !== original.on || (on && point !== original.point);

  const handleApply = async () => {
    if (!club || !isModified || !isValid) return;
    try {
      setIsLoading(true);
      const body = on ? { on: true, point: parsed } : (amountOk ? { on: false, point: parsed } : { on: false });
      const res = await client.post(`${url}/api/runningtrack/setting/trialreward/${club.clubID}`, body);
      if (!res.data || res.data.result !== 1) {
        alert('체험 보상 포인트는 1 이상 1,000,000 이하의 정수로 입력해 주세요.');
        return;
      }
      const savedOn = !!res.data.on;
      const savedAmount = String(res.data.amount != null ? res.data.amount : parsed);
      setOn(savedOn);
      setPoint(savedAmount);
      setOriginal({ on: savedOn, point: savedAmount });
      alert(savedOn ? `체험 보상을 켰습니다 — ${Number(savedAmount).toLocaleString()}P` : '체험 보상을 껐습니다. 지급하지 않고 앱의 포인트 안내도 숨깁니다.');
    } catch (error) {
      console.error('Failed to save trial reward:', error);
      alert('체험 보상 설정 저장에 실패했습니다.');
    } finally {
      setIsLoading(false);
    }
  };

  const clubName = (club && club.name) || '클럽';

  return (
    <Container>
      <Title>체험 보상 포인트 설정</Title>

      <InfoBox>
        <InfoTitle>
          체험 보상 안내
        </InfoTitle>
        <InfoList>
          <InfoItem>
            <strong>지급 조건:</strong> 러닝머신 앱에서 15분 운동을 채운 적이 없는 회원이 한 번의 운동에서 15분을 채우면 설정한 포인트를 지급합니다 (회원 한 명당 이 클럽에서 1회, 받을 때까지 운동마다 안내)
          </InfoItem>
          <InfoItem>
            <strong>끄면:</strong> 지급하지 않고, 러닝머신 앱의 체험 소개·운동 화면에서도 포인트 안내가 사라집니다
          </InfoItem>
          <InfoItem>
            <strong>켜면:</strong> 1P 이상이어야 합니다 (0P 로는 켤 수 없습니다). 설정하지 않은 클럽은 켜짐 · {TRIAL_REWARD_DEFAULT_POINT.toLocaleString()}P 입니다
          </InfoItem>
          <InfoItem>
            <strong>클럽 이름:</strong> 앱 안내 문구에 자동으로 들어갑니다 (예: "{clubName} 회원들과 화면 속 트랙에서 함께 달려보세요.")
          </InfoItem>
        </InfoList>
      </InfoBox>

      <SettingSection>
        <TimeInputContainer>
          <Label>체험 보상</Label>
          <SwitchRow>
            <SwitchButton type="button" style={switchStyle(on, true)} disabled={isLoading} onClick={() => setOn(true)}>켜짐</SwitchButton>
            <SwitchButton type="button" style={switchStyle(!on, false)} disabled={isLoading} onClick={() => setOn(false)}>꺼짐</SwitchButton>
          </SwitchRow>
        </TimeInputContainer>

        <TimeInputContainer>
          <Label>체험 보상 포인트</Label>
          <InputRow>
            <PointInput
              type="number"
              min={1}
              max={TRIAL_REWARD_MAX_POINT}
              step={1}
              value={point}
              onChange={(e) => setPoint(e.target.value)}
              disabled={isLoading || !on}
            />
            P
          </InputRow>
          {on && !amountOk && <FieldError>켜려면 1 이상 1,000,000 이하로 입력해 주세요 (0P 로는 켤 수 없습니다)</FieldError>}
        </TimeInputContainer>
      </SettingSection>

      <ApplyButton
        onClick={handleApply}
        disabled={!isModified || !isValid || isLoading}
      >
        {isLoading ? '저장 중...' : '적용하기'}
      </ApplyButton>
    </Container>
  );
};

const RunningTrackSettingComponent = () => {
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [originalStartTime, setOriginalStartTime] = useState('');
  const [originalEndTime, setOriginalEndTime] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const {
    club
  } = useSelector(({ club }) => ({
    club: club.defaultClub,
  }));

  // 초를 시간:분 형태로 변환하는 함수
  const secondsToTimeString = (seconds) => {
    if (!seconds && seconds !== 0) return '';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  };

  // 시간:분을 초로 변환하는 함수
  const timeStringToSeconds = (timeString) => {
    if (!timeString) return 0;
    const [hours, minutes] = timeString.split(':').map(Number);
    return hours * 3600 + minutes * 60;
  };

  // 운영시간 설정 데이터 로드
  const loadOperatingTime = async () => {
    if (!club) return;

    try {
      setIsLoading(true);
      const res = await client.get(`${url}/api/runningtrack/setting/time/${club.clubID}`);
      console.log('Operating time data:', res.data);

      const { startTime: startSeconds, endTime: endSeconds } = res.data;

      const startTimeStr = secondsToTimeString(startSeconds);
      const endTimeStr = secondsToTimeString(endSeconds);

      setStartTime(startTimeStr);
      setEndTime(endTimeStr);
      setOriginalStartTime(startTimeStr);
      setOriginalEndTime(endTimeStr);
    } catch (error) {
      console.error('Failed to load operating time:', error);
      alert('운영시간 데이터를 불러오는데 실패했습니다.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadOperatingTime();
  }, [club]);

  // 시간 변경 시 적용 버튼 활성화 여부 확인
  const isModified = startTime !== originalStartTime || endTime !== originalEndTime;

  // 적용하기 버튼 클릭
  const handleApply = async () => {
    if (!club || !isModified) return;

    try {
      setIsLoading(true);

      const startSeconds = timeStringToSeconds(startTime);
      const endSeconds = timeStringToSeconds(endTime);

      const res = await client.post(`${url}/api/runningtrack/setting/time/${club.clubID}`, {
        startTime: startSeconds,
        endTime: endSeconds
      });

      console.log('Operating time saved:', res.data);

      // 저장 성공 시 원본 데이터 업데이트
      setOriginalStartTime(startTime);
      setOriginalEndTime(endTime);

      alert('운영시간이 저장되었습니다.');
    } catch (error) {
      console.error('Failed to save operating time:', error);
      alert('운영시간 저장에 실패했습니다.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
    {/* 실시간 데이터 (조회 전용) */}
    <RunningTrackRealtimeComponent />

    <Container>
      <Title>러닝트랙 운영시간 설정</Title>

      <InfoBox>
        <InfoTitle>
          운영시간 설정 안내
        </InfoTitle>
        <InfoList>
          <InfoItem>
            <strong>경기 생성:</strong> 운영시간 내에만 자동으로 경기가 생성됩니다
          </InfoItem>
          <InfoItem>
            <strong>TV 디스플레이:</strong> 운영시간 이외에는 화면 보호(스크린 세이버) 모드로 전환됩니다
          </InfoItem>
          <InfoItem>
            <strong>러닝머신 앱:</strong> 운영시간 이외에는 싱글 플레이 모드만 사용 가능합니다
          </InfoItem>
        </InfoList>
      </InfoBox>

      <SettingSection>
        <TimeInputContainer>
          <Label>시작 시간</Label>
          <TimeInput
            type="time"
            value={startTime}
            onChange={(e) => setStartTime(e.target.value)}
            disabled={isLoading}
          />
        </TimeInputContainer>

        <TimeInputContainer>
          <Label>종료 시간</Label>
          <TimeInput
            type="time"
            value={endTime}
            onChange={(e) => setEndTime(e.target.value)}
            disabled={isLoading}
          />
        </TimeInputContainer>
      </SettingSection>

      <ApplyButton
        onClick={handleApply}
        disabled={!isModified || isLoading}
      >
        {isLoading ? '저장 중...' : '적용하기'}
      </ApplyButton>
    </Container>

    <TrialRewardSetting club={club} />
    </>
  );
};

export default RunningTrackSettingComponent;
